import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDateTime12 } from "@/lib/time";
import { Wallet, IndianRupee, TrendingUp, AlertTriangle, ArrowDownLeft, ChevronDown, Check, ShieldAlert, RotateCcw } from "lucide-react";

function CustomDropdown({ value, options, onChange, placeholder = "Select", className = "" }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => String(o.value) === String(value));

  return (
    <div className={`relative w-full ${open ? "z-30" : "z-10"}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/20 bg-black text-white text-sm outline-none focus:border-hgold hover:border-hgold/70 transition-all ${className}`}
      >
        <span className={`truncate ${selectedOption ? "text-white font-medium" : "text-white/40"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} className={`transform transition-transform duration-300 text-amber-400 shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 mt-2 rounded-2xl border border-amber-400/80 bg-[#0a0a0a] shadow-2xl shadow-yellow-950/60 z-50 p-2"
          >
            <div className="max-h-56 overflow-y-auto pr-1.5 space-y-1">
              {options.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 text-xs font-bold tracking-wide flex items-center justify-between rounded-xl transition-colors ${
                      isSelected
                        ? "bg-amber-500/25 text-amber-300 border-l-4 border-amber-400"
                        : "text-white/80 hover:bg-white/10 hover:text-amber-300"
                    }`}
                  >
                    <span className="truncate pr-2">{opt.label}</span>
                    {isSelected && <Check size={15} className="text-amber-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AdminWallets() {
  const { user } = useAuth();
  const [therapists, setTherapists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, mode: "cash", notes: "" });
  const [fineForm, setFineForm] = useState({ amount: 0, reason: "" });
  const [loading, setLoading] = useState(true);

  const loadTherapists = async () => {
    setLoading(true);
    try {
      const q = user.role === "branch_admin" ? `?branch_id=${user.branch_id}` : "";
      const r = await api.get(`/therapists${q}`);
      setTherapists(r.data);
      if (r.data.length && !selected) setSelected(r.data[0].id);
    } catch {
      toast.error("Failed to load therapists");
    } finally {
      setLoading(false);
    }
  };

  const loadWallet = async (tid) => {
    if (!tid) return;
    const r = await api.get(`/admin/wallet/${tid}`);
    setWallet(r.data);
  };

  useEffect(() => { loadTherapists(); }, []);
  useEffect(() => { loadWallet(selected); }, [selected]);

  const pay = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post("/admin/wallet/payment", { therapist_id: selected, amount: Number(payForm.amount), mode: payForm.mode, notes: payForm.notes });
      toast.success("Payment recorded successfully");
      setPayForm({ amount: 0, mode: "cash", notes: "" });
      loadWallet(selected);
    } catch { toast.error("Failed to record payment"); }
  };

  const fine = async () => {
    if (!fineForm.amount || !fineForm.reason) return toast.error("Select reason & amount");
    try {
      await api.post("/admin/wallet/fine", { therapist_id: selected, amount: Number(fineForm.amount), reason: fineForm.reason });
      toast.success("Fine issued successfully");
      setFineForm({ amount: 0, reason: "" });
      loadWallet(selected);
    } catch { toast.error("Master admin authorization required"); }
  };

  const reverse = async (id) => {
    if (!confirm("Reverse this entry?")) return;
    try {
      await api.delete(`/admin/wallet/entry/${id}`);
      toast.success("Transaction entry reversed");
      loadWallet(selected);
    } catch { toast.error("Master admin authorization required"); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-emerald-400 font-bold uppercase tracking-widest animate-pulse">Loading Wallets...</div>
      </div>
    );
  }

  const therapistOptions = therapists.map((t) => ({ label: `${t.display_name} (${t.phone || "No Phone"})`, value: t.id }));
  const modeOptions = [
    { label: "Cash Payment", value: "cash" },
    { label: "UPI Online", value: "upi" },
    { label: "Bank Transfer", value: "bank" },
  ];
  const fineReasonOptions = [
    { label: "Late Arrival", value: "Late Arrival" },
    { label: "Customer Complaint", value: "Complaint" },
    { label: "Policy Violation", value: "Policy Violation" },
    { label: "No Show", value: "No Show" },
  ];

  return (
    <div data-testid="admin-wallets" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
            <Wallet size={16} /> FINANCIALS & PAYOUTS
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Therapist Wallet
          </h1>
        </div>

        {therapists.length > 0 && (
          <div className="w-full sm:w-72">
            <label className="text-xs font-semibold text-white/70 block mb-1 uppercase tracking-wider">Select Therapist</label>
            <CustomDropdown
              value={selected}
              options={therapistOptions}
              onChange={(val) => setSelected(val)}
              placeholder="Select Therapist"
            />
          </div>
        )}
      </motion.div>

      {therapists.length === 0 && (
        <div className="border border-white/10 p-16 text-center text-white/50 bg-hpaper rounded-2xl font-medium">
          No therapists found. Please add a therapist in the Therapists panel first.
        </div>
      )}

      {wallet && (
        <>
          {/* KPI Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-5"
          >
            <div className="p-6 rounded-2xl border border-amber-400/80 bg-gradient-to-b from-amber-950/50 via-black/90 to-amber-950/20 shadow-lg shadow-amber-950/20">
              <div className="flex justify-between items-center text-[10px] uppercase tracking-widest font-extrabold text-amber-400 mb-2">
                <span>Current Balance</span>
                <IndianRupee size={18} />
              </div>
              <div className="text-3xl font-heading font-extrabold text-amber-300">{inr(wallet.balance)}</div>
            </div>

            <div className="p-6 rounded-2xl border border-emerald-400/80 bg-gradient-to-b from-emerald-950/50 via-black/90 to-emerald-950/20 shadow-lg shadow-emerald-950/20">
              <div className="flex justify-between items-center text-[10px] uppercase tracking-widest font-extrabold text-emerald-400 mb-2">
                <span>Lifetime Earning</span>
                <TrendingUp size={18} />
              </div>
              <div className="text-3xl font-heading font-extrabold text-emerald-400">{inr(wallet.lifetime_earning)}</div>
            </div>

            <div className="p-6 rounded-2xl border border-cyan-400/80 bg-gradient-to-b from-cyan-950/50 via-black/90 to-cyan-950/20 shadow-lg shadow-cyan-950/20">
              <div className="flex justify-between items-center text-[10px] uppercase tracking-widest font-extrabold text-cyan-400 mb-2">
                <span>Paid Out</span>
                <ArrowDownLeft size={18} />
              </div>
              <div className="text-3xl font-heading font-extrabold text-cyan-400">{inr(wallet.paid)}</div>
            </div>

            <div className="p-6 rounded-2xl border border-red-500/80 bg-gradient-to-b from-red-950/50 via-black/90 to-red-950/20 shadow-lg shadow-red-950/20">
              <div className="flex justify-between items-center text-[10px] uppercase tracking-widest font-extrabold text-red-400 mb-2">
                <span>Total Fines</span>
                <AlertTriangle size={18} />
              </div>
              <div className="text-3xl font-heading font-extrabold text-red-400">{inr(wallet.fines)}</div>
            </div>
          </motion.div>

          {/* Action Forms Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {/* Release Payment */}
            <div className="rounded-2xl border border-hgold/70 p-6 lg:p-8 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 space-y-5">
              <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold pb-3 border-b border-white/10">
                RELEASE PAYMENT
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Payment Amount (₹)</label>
                  <input
                    type="number"
                    placeholder="Enter amount to pay"
                    value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    data-testid="wallet-pay-amount"
                    className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Payment Mode</label>
                  <CustomDropdown
                    value={payForm.mode}
                    options={modeOptions}
                    onChange={(val) => setPayForm({ ...payForm, mode: val })}
                    placeholder="Payment mode"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Optional Notes</label>
                  <input
                    placeholder="Transaction reference / note..."
                    value={payForm.notes}
                    onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                    className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                  />
                </div>

                <button
                  onClick={pay}
                  data-testid="wallet-pay-submit"
                  className="gold-btn w-full justify-center py-4 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40"
                >
                  Release Payment
                </button>
              </div>
            </div>

            {/* Issue Fine */}
            <div className="rounded-2xl border border-red-500/70 p-6 lg:p-8 bg-gradient-to-b from-red-950/30 via-black/90 to-red-950/20 shadow-xl shadow-red-950/20 space-y-5">
              <div className="text-xs uppercase tracking-[0.25em] text-red-400 font-extrabold pb-3 border-b border-white/10 flex items-center gap-2">
                <ShieldAlert size={16} /> ISSUE FINE
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Fine Amount (₹)</label>
                  <input
                    type="number"
                    placeholder="Enter fine amount"
                    value={fineForm.amount}
                    onChange={(e) => setFineForm({ ...fineForm, amount: e.target.value })}
                    data-testid="wallet-fine-amount"
                    className="w-full bg-black/70 border border-white/20 focus:border-red-500 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold text-red-400"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Select Reason</label>
                  <CustomDropdown
                    value={fineForm.reason}
                    options={fineReasonOptions}
                    onChange={(val) => setFineForm({ ...fineForm, reason: val })}
                    placeholder="Select fine reason"
                  />
                </div>

                <button
                  onClick={fine}
                  data-testid="wallet-fine-submit"
                  className="w-full border border-red-500/50 bg-red-950/40 hover:bg-red-900/60 text-red-300 py-4 rounded-xl text-xs uppercase tracking-wider font-extrabold transition-all shadow-md shadow-red-950/40"
                >
                  Issue Fine
                </button>
              </div>
            </div>
          </motion.div>

          {/* Transaction History Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl border border-hgold/70 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 overflow-hidden p-6 lg:p-8"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-6">TRANSACTION HISTORY</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b-2 border-white/10 text-amber-400 font-extrabold uppercase text-[11px] tracking-wider">
                    <th className="pb-4 font-bold">Date & Time</th>
                    <th className="pb-4 font-bold">Therapist</th>
                    <th className="pb-4 font-bold">Type</th>
                    <th className="pb-4 font-bold">Amount</th>
                    <th className="pb-4 font-bold">Details</th>
                    <th className="pb-4 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {wallet.entries?.map((e) => (
                    <tr key={e.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-4 text-white/70 text-xs font-medium">{fmtDateTime12(e.created_at)}</td>
                      <td className="py-4 font-bold text-white">{wallet.therapist_name}</td>
                      <td className="py-4">
                        <span className={`text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full inline-block ${
                          e.type === "earning" ? "bg-emerald-950/80 text-emerald-400 border border-emerald-400/50 shadow-sm" :
                          e.type === "payment" ? "bg-cyan-950/80 text-cyan-400 border border-cyan-400/50 shadow-sm" :
                          "bg-red-950/80 text-red-400 border border-red-500/50 shadow-sm"
                        }`}>
                          {e.type}
                        </span>
                      </td>
                      <td className="py-4 font-bold text-amber-300 text-base">{inr(e.amount)}</td>
                      <td className="py-4 text-white/70 text-xs">{e.notes || e.reason || "—"}</td>
                      <td className="py-4">
                        <button
                          onClick={() => reverse(e.id)}
                          className="flex items-center gap-1 text-red-400 hover:text-red-300 border border-red-500/40 bg-red-950/20 px-3 py-1.5 rounded-lg text-xs uppercase font-extrabold tracking-wider transition-all"
                        >
                          <RotateCcw size={12} /> Reverse
                        </button>
                      </td>
                    </tr>
                  ))}
                  {wallet.entries?.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-12 text-white/50 text-base font-medium">
                        No transactions recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
